import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/shared/utils';
import {
  Play,
  Pause,
  RotateCcw,
  MapPin,
  Navigation,
  Truck,
  Package,
  CheckCircle,
  AlertTriangle,
  Thermometer,
  Clock } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Slider } from '@/shared/components/ui/slider';

// Stop marker component - Uber style numbered pins
const StopMarker = ({ stop, index, isActive, isCompleted, position }) =>
<div
  className={cn(
    "absolute transform -translate-x-1/2 -translate-y-full transition-all duration-300 z-10",
    isActive && "scale-110 z-20"
  )}
  style={{ left: `${position.x}%`, top: `${position.y}%` }}>
  
    <div className="relative">
      {/* Pin */}
      <div className={cn(
      "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg",
      isCompleted ? "bg-emerald-500" : isActive ? "bg-blue-500 animate-pulse" : "bg-gray-400"
    )}>
        {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
      </div>
      {/* Pin tail */}
      <div className={cn(
      "absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px]",
      isCompleted ? "border-t-emerald-500" : isActive ? "border-t-blue-500" : "border-t-gray-400"
    )} style={{ top: '28px' }} />
      {/* Label */}
      <div className={cn(
      "absolute top-10 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 py-1 rounded shadow text-xs font-medium",
      isActive && "bg-blue-50 border border-blue-200"
    )}>
        {stop.name}
      </div>
    </div>
  </div>;


// Driver/vehicle marker - animated current position
const VehicleMarker = ({ position, isMoving }) =>
<div
  className="absolute transform -translate-x-1/2 -translate-y-1/2 z-30 transition-all duration-500"
  style={{ left: `${position.x}%`, top: `${position.y}%` }}>
  
    {/* Pulse ring */}
    {isMoving &&
  <div className="absolute inset-0 w-12 h-12 -ml-3 -mt-3 rounded-full bg-blue-500 opacity-25 animate-ping" />
  }
    {/* Vehicle icon container */}
    <div className={cn(
    "w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white",
    isMoving ? "bg-blue-600" : "bg-gray-600"
  )}>
      <Truck className="h-5 w-5 text-white" />
    </div>
  </div>;


// Event marker on map
const EventMarker = ({ event, position }) => {const { t } = useTranslation("ezparcel");
  const getEventStyle = () => {
    switch (event.type) {
      case 'TEMP_EXCURSION':
        return { bg: 'bg-red-500', icon: Thermometer };
      case 'UNSCHEDULED_STOP':
        return { bg: 'bg-amber-500', icon: AlertTriangle };
      case 'SPEED_WARNING':
        return { bg: 'bg-orange-500', icon: Navigation };
      default:
        return { bg: 'bg-gray-500', icon: MapPin };
    }
  };

  const { bg, icon: Icon } = getEventStyle();

  return (
    <div
      className="absolute transform -translate-x-1/2 -translate-y-1/2 z-15"
      style={{ left: `${position.x}%`, top: `${position.y}%` }}>
      
      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center shadow", bg)}>
        <Icon className="h-3 w-3 text-white" />
      </div>
    </div>);

};

export const RouteReplayMap = ({
  gpsData,
  currentIndex = 0,
  isPlaying = false,
  onPlayPause,
  onSeek,
  onReset,
  showEvents = true
}) => {const { t } = useTranslation("ezparcel");
  const [hoveredStop, setHoveredStop] = useState(null);

  // Calculate positions for stops and current vehicle
  // This creates a visual representation - in production would use real map coords
  const getStopPositions = () => {
    if (!gpsData?.stops) return [];
    const totalStops = gpsData.stops.length + 2; // +2 for start and end
    return [
    { x: 10, y: 50 }, // Start warehouse
    ...gpsData.stops.map((_, i) => ({
      x: 15 + (i + 1) * (70 / totalStops),
      y: 30 + (i % 2 === 0 ? 20 : -10)
    })),
    { x: 90, y: 50 } // End
    ];
  };

  const stopPositions = getStopPositions();

  // Calculate current vehicle position based on playback index
  const getCurrentPosition = () => {
    if (!gpsData?.breadcrumbs || gpsData.breadcrumbs.length === 0) {
      return { x: 10, y: 50 };
    }
    const totalPoints = gpsData.breadcrumbs.length;
    const progress = currentIndex / (totalPoints - 1);
    // Interpolate along the route
    return {
      x: 10 + progress * 80,
      y: 50 + Math.sin(progress * Math.PI * 2) * 15
    };
  };

  const vehiclePosition = getCurrentPosition();
  const currentBreadcrumb = gpsData?.breadcrumbs?.[currentIndex];
  const isMoving = currentBreadcrumb?.speed > 0;

  // Get event markers
  const eventMarkers = gpsData?.breadcrumbs?.filter((b) =>
  b.event && ['TEMP_EXCURSION', 'UNSCHEDULED_STOP', 'SPEED_WARNING'].includes(b.event)
  ).map((b, i) => {
    const idx = gpsData.breadcrumbs.indexOf(b);
    const progress = idx / (gpsData.breadcrumbs.length - 1);
    return {
      ...b,
      position: {
        x: 10 + progress * 80,
        y: 50 + Math.sin(progress * Math.PI * 2) * 15
      }
    };
  }) || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="route-replay-map">
      {/* Map Container */}
      <div className="relative h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {/* Grid pattern background - simulating map tiles */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#94a3b8" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Route line */}
        <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 5 }}>
          <defs>
            <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
          </defs>
          {/* Main route path */}
          <path
            d={`M ${10}% ${50}% Q ${25}% ${70}%, ${40}% ${40}% T ${60}% ${55}% T ${90}% ${50}%`}
            fill="none"
            stroke="url(#routeGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="10,5"
            className="opacity-60" />
          
          {/* Traveled path (solid) */}
          <path
            d={`M ${10}% ${50}% Q ${25}% ${70}%, ${40}% ${40}% T ${60}% ${55}% T ${90}% ${50}%`}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDashoffset={`${100 - currentIndex / (gpsData?.breadcrumbs?.length - 1 || 1) * 100}%`}
            style={{
              strokeDasharray: '100%',
              transition: 'stroke-dashoffset 0.3s ease'
            }} />
          
        </svg>
        
        {/* Event markers */}
        {showEvents && eventMarkers.map((event, idx) =>
        <EventMarker key={idx} event={event} position={event.position} />
        )}
        
        {/* Stop markers */}
        {gpsData?.stops?.map((stop, idx) => {
          const completedStops = gpsData.breadcrumbs?.
          slice(0, currentIndex + 1).
          filter((b) => b.event === 'STOP_DEPARTURE').
          length || 0;

          return (
            <StopMarker
              key={stop.id}
              stop={stop}
              index={idx}
              isActive={completedStops === idx}
              isCompleted={completedStops > idx}
              position={stopPositions[idx + 1]} />);


        })}
        
        {/* Start marker */}
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
          style={{ left: '10%', top: '50%' }}>
          
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg border-2 border-white">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div className="absolute top-12 left-1/2 -translate-x-1/2 whitespace-nowrap bg-emerald-50 px-2 py-1 rounded shadow text-xs font-medium text-emerald-700 border border-emerald-200">{t("warehouse", "Warehouse")}

          </div>
        </div>
        
        {/* End marker */}
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
          style={{ left: '90%', top: '50%' }}>
          
          <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center shadow-lg border-2 border-white">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
          <div className="absolute top-12 left-1/2 -translate-x-1/2 whitespace-nowrap bg-violet-50 px-2 py-1 rounded shadow text-xs font-medium text-violet-700 border border-violet-200">{t("complete", "Complete")}

          </div>
        </div>
        
        {/* Vehicle marker */}
        <VehicleMarker position={vehiclePosition} isMoving={isMoving} />
        
        {/* Current info overlay */}
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 max-w-xs">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <Truck className="h-4 w-4 text-blue-600" />
            {gpsData?.driverName || 'Driver'}
          </div>
          {currentBreadcrumb &&
          <div className="mt-2 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>{t("speed", "Speed:")}</span>
                <span className="font-medium">{currentBreadcrumb.speed}{t("kmh", "km/h")}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("temp", "Temp:")}</span>
                <span className={cn(
                "font-medium",
                currentBreadcrumb.temp > 8 ? "text-red-600" : "text-emerald-600"
              )}>
                  {currentBreadcrumb.temp}{t("c", "\xB0C")}
              </span>
              </div>
              <div className="flex justify-between">
                <span>{t("time", "Time:")}</span>
                <span className="font-medium">
                  {new Date(currentBreadcrumb.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          }
        </div>
        
        {/* Legend */}
        <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow p-2">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span>{t("completed", "Completed")}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span>{t("current", "Current")}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              <span>{t("pending", "Pending")}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Playback Controls - Uber-style bottom bar */}
      <div className="border-t border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-4">
          {/* Play/Pause button */}
          <Button
            variant="outline"
            size="icon"
            onClick={onPlayPause}
            className="h-10 w-10 rounded-full"
            data-testid="replay-play-pause">
            
            {isPlaying ?
            <Pause className="h-5 w-5" /> :

            <Play className="h-5 w-5 ml-0.5" />
            }
          </Button>
          
          {/* Reset button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onReset}
            className="h-10 w-10 rounded-full"
            data-testid="replay-reset">
            
            <RotateCcw className="h-4 w-4" />
          </Button>
          
          {/* Timeline slider */}
          <div className="flex-1 px-2">
            <Slider
              value={[currentIndex]}
              max={gpsData?.breadcrumbs?.length - 1 || 0}
              step={1}
              onValueChange={(value) => onSeek?.(value[0])}
              className="cursor-pointer"
              data-testid="replay-slider" />
            
          </div>
          
          {/* Time display */}
          <div className="flex items-center gap-2 text-sm text-gray-600 min-w-[120px] justify-end">
            <Clock className="h-4 w-4" />
            <span className="font-mono">
              {currentBreadcrumb ?
              new Date(currentBreadcrumb.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
              '--:--'
              }
            </span>
          </div>
        </div>
        
        {/* Progress info */}
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span>{t("point", "Point")}
            {currentIndex + 1}{t("of", "of")}{gpsData?.breadcrumbs?.length || 0}
          </span>
          <span>
            {gpsData?.totalDistance || 0}{t("kmTotal", "km total \xB7")}{gpsData?.stops?.length || 0}{t("stops", "stops")}
          </span>
        </div>
      </div>
    </div>);

};